import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Maximize, Minimize } from "lucide-react";
import { Button } from "../ui/button";
import { Alert, AlertDescription } from "../ui/alert";
import usePoll from "../../hooks/usePoll";
import { triviaApi } from "../../services/api";
import { apiErrorMessage } from "../../services/apiError";
import BouncePen from "./BouncePen";
import ChoiceTiles from "./ChoiceTiles";
import Countdown from "./Countdown";
import Leaderboard from "./Leaderboard";
import Podium from "./Podium";
import { DICTIONARY_URL, playAddress, singleClick } from "./game";

/**
 * /trivia/:id — the big screen (projects/trivia). It asks the server what
 * to show once a second; the server decides when a question turns to its
 * reveal (D8), so this page only draws and sends the host's three moves:
 * Start, Reveal now, Next.
 */
function TriviaHostGame() {
  const { id } = useParams();
  const navigate = useNavigate();
  const stageRef = useRef(null);
  // The phase's main button (Start, Reveal now, Next, New game).
  const primaryRef = useRef(null);
  const [game, setGame] = useState(null);
  const [missing, setMissing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [fullscreen, setFullscreen] = useState(false);
  // A move's response is newer than any poll already in flight: the move
  // bumps this when it lands, and a poll that was asked before then (sent
  // before or during the move) is dropped.
  const generation = useRef(0);

  const tick = useCallback(
    async ({ stale }) => {
      const asked = generation.current;
      try {
        const { data } = await triviaApi.get(id);
        if (stale() || asked !== generation.current) return;
        setGame(data);
      } catch (err) {
        if (stale()) return;
        if (err?.response?.status === 404) {
          setMissing(true);
          return;
        }
        throw err;
      }
    },
    [id],
  );
  const { status } = usePoll(tick, {
    intervalMs: 1000,
    immediate: true,
    resetKey: id,
    enabled: !missing && game?.phase !== "finished",
  });

  // Escape leaves full screen without the button: follow the browser.
  useEffect(() => {
    const sync = () => setFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener("fullscreenchange", sync);
    return () => document.removeEventListener("fullscreenchange", sync);
  }, []);

  // A screen change unmounts the button that had focus (Start, Show the
  // final scores): put a keyboard host on the new screen's main button
  // rather than on <body> (the #325 rule). Only when focus was lost, so a
  // poll never steals it from somewhere the host put it.
  const phase = game?.phase;
  const index = game?.index;
  useEffect(() => {
    const lost = !document.activeElement || document.activeElement === document.body;
    if (lost) primaryRef.current?.focus();
  }, [phase, index]);

  // An error belongs to the screen it happened on.
  useEffect(() => setError(null), [phase, index]);

  async function move(action) {
    setBusy(true);
    setError(null);
    try {
      const { data } = await action();
      generation.current += 1;
      setGame(data);
    } catch (err) {
      setError(apiErrorMessage(err, "That didn't work. Try again."));
    } finally {
      setBusy(false);
    }
  }

  async function toggleFullscreen() {
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      } else {
        await stageRef.current?.requestFullscreen();
      }
    } catch {
      // Some browsers refuse; the page still works at its normal size.
    }
  }

  if (missing) {
    return (
      <div className="page-container space-y-4 py-6">
        <p>This game doesn&apos;t exist, or it isn&apos;t yours.</p>
        <Link className="underline" to="/trivia">
          Start a new game
        </Link>
      </div>
    );
  }
  if (!game) {
    return <p className="page-container py-6 text-muted-foreground">Loading the game…</p>;
  }

  const last = game.question_number >= game.question_count;

  return (
    <div
      ref={stageRef}
      className="flex min-h-full flex-1 flex-col gap-6 overflow-auto bg-background p-4 md:p-8"
    >
      <div className="flex flex-wrap items-center gap-3 text-muted-foreground">
        <span className="font-display text-xl text-foreground">AI Term Trivia</span>
        {game.phase !== "lobby" && (
          <span>
            Join code <strong className="font-mono text-foreground">{game.code}</strong>
          </span>
        )}
        {status === "reconnecting" && <span role="status">Reconnecting…</span>}
        <Button variant="outline" size="sm" className="ms-auto" onClick={toggleFullscreen}>
          {fullscreen ? <Minimize aria-hidden="true" /> : <Maximize aria-hidden="true" />}
          {fullscreen ? "Leave full screen" : "Full screen"}
        </Button>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {game.phase === "lobby" && (
        <section className="space-y-6" aria-label="Lobby">
          <div className="flex flex-col items-center gap-2 text-center">
            <p className="text-2xl">
              On your phone, go to <strong className="text-foreground">{playAddress()}</strong> and
              enter
            </p>
            <p className="font-mono text-7xl font-bold tracking-[0.2em] md:text-8xl">
              <span className="sr-only">Join code </span>
              <span data-testid="join-code">{game.code}</span>
            </p>
          </div>
          <BouncePen players={game.players} />
          <div className="flex flex-wrap items-center justify-between gap-4">
            <p className="text-xl" role="status">
              {game.player_count === 0
                ? "Waiting for players to join…"
                : `${game.player_count} ${game.player_count === 1 ? "player" : "players"} in`}
            </p>
            <Button
              size="lg"
              className="h-12 px-8 text-lg"
              ref={primaryRef}
              {...singleClick(() => move(() => triviaApi.start(game.id)))}
              disabled={busy || game.player_count === 0}
            >
              Start the game
            </Button>
          </div>
        </section>
      )}

      {(game.phase === "question" || game.phase === "reveal") && (
        <section className="space-y-6" aria-label={`Question ${game.question_number}`}>
          <div className="flex flex-wrap items-center gap-4">
            <p className="text-xl text-muted-foreground">
              Question {game.question_number} of {game.question_count}
            </p>
            {game.phase === "question" && (
              <p className="text-xl" role="status">
                {game.answered_count} of {game.player_count} answered
              </p>
            )}
          </div>
          {game.phase === "question" && (
            <Countdown
              secondsLeft={game.seconds_left}
              total={game.seconds_per_question}
              questionKey={game.index}
              big
            />
          )}
          {game.question ? (
            <>
              <h1 className="font-display text-3xl leading-snug font-semibold md:text-5xl">
                {game.question.prompt}
              </h1>
              <ChoiceTiles
                choices={game.question.choices}
                correct={game.reveal?.correct ?? null}
                counts={game.reveal?.counts ?? null}
                big
              />
            </>
          ) : (
            <p className="text-2xl">This question was taken out of the game. Move on to the next one.</p>
          )}

          {game.phase === "reveal" && game.reveal?.explanation && (
            <p className="rounded-lg border bg-card p-4 text-xl md:text-2xl">{game.reveal.explanation}</p>
          )}
          {game.phase === "reveal" && game.leaderboard.length > 0 && (
            <div className="max-w-2xl space-y-2">
              <h2 className="font-display text-2xl font-semibold">Top {game.leaderboard.length}</h2>
              <Leaderboard rows={game.leaderboard} big />
            </div>
          )}

          <div className="flex justify-end">
            {game.phase === "question" ? (
              <Button
                variant="outline"
                size="lg"
                className="h-12 px-6 text-lg"
                ref={primaryRef}
                {...singleClick(() => move(() => triviaApi.reveal(game.id)))}
                disabled={busy}
              >
                Reveal the answer now
              </Button>
            ) : (
              <Button
                size="lg"
                className="h-12 px-8 text-lg"
                ref={primaryRef}
                {...singleClick(() => move(() => triviaApi.next(game.id, game.index)))}
                disabled={busy}
              >
                {last ? "Show the final scores" : "Next question"}
              </Button>
            )}
          </div>
        </section>
      )}

      {game.phase === "finished" && (
        <section className="space-y-8" aria-label="Final scores">
          <h1 className="text-center font-display text-4xl font-semibold md:text-5xl">Final scores</h1>
          {game.leaderboard.length > 0 ? (
            <Podium rows={game.leaderboard} />
          ) : (
            <p className="text-center text-xl">Nobody played this one.</p>
          )}
          <div className="flex justify-center">
            <Button
              ref={primaryRef}
              size="lg"
              className="h-12 px-8 text-lg"
              onClick={() => navigate("/trivia")}
            >
              New game
            </Button>
          </div>
        </section>
      )}

      <p className="mt-auto text-sm text-muted-foreground">
        Questions written from Matt Pocock&apos;s{" "}
        <a className="underline" href={DICTIONARY_URL} target="_blank" rel="noreferrer">
          AI Coding Dictionary
        </a>
        .
      </p>
    </div>
  );
}

export default TriviaHostGame;
